import React from 'react'
import { type To } from 'react-router-dom'

import { ButtonSizeType } from '../main/components/button'
import { DateRangeBaseOption } from '../main/components/timeframe-bar/daterange-select'
import { TimeframeBaseOption } from '../main/components/timeframe-bar/timeframe-select'
import { TIMEFRAME } from '../main/constants'
import { Instrument } from '../main/types'

export enum SettingsTab {
  Chart = 'chart',
  Colors = 'colors',
}

export enum SidebarDirection {
  vertical,
  horizontal,
}

export type ChartLayoutProps = {
  config: object
  onRegisterChartLayoutModel?: (chartLayout: any) => void
  shouldResize?: boolean
  parentElement?: HTMLElement
  isWithNotifications?: boolean
}

export type ChartLayoutHeaderProps = {
  model: any
  onSettingsOpen: () => void
  hasPublish: boolean
}

export type SidebarProps = {
  onShouldResize: () => void
  direction: SidebarDirection
  tabsClassName?: string
}

interface ChartsLib {
  FREE_DATE_RANGES: string[]
  ChartLayout: React.FC<ChartLayoutProps>
  ChartLayoutHeader: React.FC<ChartLayoutHeaderProps>
  ChartLayoutWithGlobalState: React.FC<ChartLayoutProps>
  Sidebar: React.FC<SidebarProps>

  chartSettingsAsyncModule: {
    cacheKey: string
    importFn: () => Promise<{
      ChartSettings: React.FC<{
        onClose: () => void
        onActiveSettingsTabChange?: (settingsComponent: SettingsTab) => void
        activeSettingsTab?: SettingsTab
      }>

      PreventClosingUnsavedChanges: React.FC<{
        onHide: () => void
        children: (props: { checkForUnsavedChangesAndClose: () => void }) => React.ReactElement
      }>

      useChartLayoutGlobalModel: () => {
        chartLayoutModel: any | null
        chartLayoutModelConfigSnapshot: Record<string, any> | null
        lastGlobalRerender: number
        setChartLayoutModel: (chartLayoutModel: any) => void
        getChartLayoutModelConfig: (props: { fallback: Record<string, any> }) => Record<string, any>
        forceGlobalRerender: () => void
        generateNewLayoutModelConfigSnapshot: () => void
      }
    }>
  }

  StockDetailTimeframeBar: React.FC<{
    size?: ButtonSizeType
    className?: string
    isDateRangeAvailable: boolean
    isPremium: boolean
    isLoadingOverride?: boolean
    isScrollIntoViewEnabled?: boolean
    onTimeframeSelect?: (timeframe: TimeframeBaseOption<TIMEFRAME>) => void
    onDateRangeSelect?: (timeframe: DateRangeBaseOption) => void
  }>

  useAsyncModule: <T, SuspenseMode extends boolean>(props: {
    importFn: () => Promise<T>
    cacheKey: string
    shouldLoadModule?: boolean
    isSuspenseMode?: SuspenseMode
  }) => [SuspenseMode extends true ? T : T | null, { isLoading: boolean; loadModule: () => Promise<T> }]

  withSuspense: <P extends Record<string, any>>(
    Component: React.ComponentType<P>,
    fallback?: NonNullable<React.ReactNode> | null
  ) => (props: P) => React.ReactElement

  Quote: {
    create(quote: any): any
    select: (query: (quote: any) => boolean) => any
    deleteAll: () => void
    findByAttribute: (attribute: string, value: any) => any
    get(params: { ticker: string; instrument: Instrument; timeframe: TIMEFRAME; options: any }): any
    getAll: (tickersAndTimeframes: any) => any
    all: () => any[]
    bind(ev: string, callback: (obj: any, ...rest: any[]) => any): any
    unbind(ev: string, callback: (obj: any, ...rest: any[]) => any): any
  }

  Settings: {
    MarketSentiment: any
    Indicator: any
    Chart: any
    Thumb: any
    Element: any
    Colors: string[]
    updateColors: (type: string, name: string, theme: 'light' | 'dark', value: string) => void
  }

  Utils: {
    dateFromUnixTimestamp: (date: string) => Date
  }

  convertTa: {
    getPanes: (taConfig: any) => any
  }

  useModelState: <Model>(
    model: Model | null,
    options: { watchProperties?: Array<keyof Model>; listenOnEvents?: string[] }
  ) => Model | null

  getTimeframeGroupsWithHref: ({
    isPremium,
    generatePath,
  }: {
    isPremium: boolean
    generatePath: (timeframe: TimeframeBaseOption<TIMEFRAME>) => To
  }) => Array<{ label: string; timeframes: TimeframeBaseOption<TIMEFRAME>[] }>

  getIntradayChartConfig: (props: {
    hasPremarket: boolean
    hasAftermarket: boolean
    maxWidth: number
    timeframeMinutes: number
    barWidthWithMargin: number
  }) => {
    fullChartWidth: number
    chartWidthWithOffset: number
    offsetInPx: number
    offsetChartAfter: number
  }

  withCompleteLayoutModelInit: (Component: any) => (props: { config: any }) => JSX.Element | null
  withQuotePolling: (Component: any) => (props: { chartLayoutModel: any }) => JSX.Element | null
}

// @ts-ignore
const chartsLib = (window.FLibs.Charts as ChartsLib) ?? {}

export const FREE_DATE_RANGES = chartsLib.FREE_DATE_RANGES
export const ChartLayout = chartsLib.ChartLayout
export const ChartLayoutWithGlobalState = chartsLib.ChartLayoutWithGlobalState
export const ChartLayoutHeader = chartsLib.ChartLayoutHeader
export const Sidebar = chartsLib.Sidebar
export const chartSettingsAsyncModule = chartsLib.chartSettingsAsyncModule
export const useAsyncModule = chartsLib.useAsyncModule
export const withSuspense = chartsLib.withSuspense
export const StockDetailTimeframeBar = chartsLib.StockDetailTimeframeBar
export const Quote = chartsLib.Quote
export const Settings = chartsLib.Settings
export const Utils = chartsLib.Utils
export const convertTa = chartsLib.convertTa
export const useModelState = chartsLib.useModelState
export const getTimeframeGroupsWithHref = chartsLib.getTimeframeGroupsWithHref
export const getIntradayChartConfig = chartsLib.getIntradayChartConfig
export const withCompleteLayoutModelInit = chartsLib.withCompleteLayoutModelInit
export const withQuotePolling = chartsLib.withQuotePolling
